import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Papa from "papaparse";
import { Button, Col, Form, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import Search from "../../../../components/Search";
import AlertMessage from "../../../../components/AlertMessage";
import ModalGlobal from "../../../../components/AppModal/ModalGlobal";
import { useAlert } from "../../../../hooks/useAlert";
import { validateFileExtension, validateFileWeight } from "../../../../utils/validators/FileValidator";
import { RootState } from "../../../../redux/store";
import { HistoricalTableKey, setCurrentPage, setSearchText } from "../../../../redux/historicalSlice";
import { ApiErrorResponse } from "../../../../types/ApiErrorResponse";
import { Pagination, PaginationFilterDto } from "../../../../types/Pagination";
import ListTable from "../components/ListTable";
import { activeData, deleteData } from "../api/basicGenericTable";
import { useGetCampaigns } from "../hooks/useGetCampaings";
import { useGenericModal } from "../hooks/useGenericModal";
import { UseGetCampaingsObjectResult } from "../../campaign/types";
import { Actions, ListFields, PaginationList } from "../types";
import { FilterTableKey, setFilterByOnlyActiveRecords } from "../../../../redux/filtersSlice";

type UploadContext = {
    file: File;
    closeModal: () => void;
    refetchData: () => Promise<void>;
    showAlert: (config: { message: string; type?: "success" | "danger" | "warning" | "info"; duration?: number; autoHide?: boolean; }) => void;
    t: (key: string, options?: Record<string, unknown>) => string;
};

type CsvUploadConfig = {
    modalTitleKey: string;
    modalSubtitleKey: string;
    modalSubtitle2Key?: string;
    onUpload: (context: UploadContext) => Promise<void>;
    useCampaignOptions?: boolean;
    showOptions?: boolean;
    saveTextKey?: string;
    closeTextKey?: string;
};

type GenericDictionaryPageProps = {
    tableKey: HistoricalTableKey;
    createTableKey?: HistoricalTableKey;
    filterTableKey?: FilterTableKey;
    createRequestKey?: string;
    fetchData: (body: PaginationFilterDto) => Promise<Pagination<ListFields>>;
    saveData?: (formData: FormData) => Promise<void>; 
    itemMapper?: (item: ListFields) => ListFields;
    titleKey?: string;
    newButtonLabel?: string | ((args: { tableKey: HistoricalTableKey; tableName: string; t: (key: string, options?: Record<string, unknown>) => string; }) => string);
    onCreateNew?: (args: { tableKey: HistoricalTableKey; createTableKey: HistoricalTableKey; }) => void;
    onView?: (id: number) => void;
    onEdit?: (id: number, tableName: HistoricalTableKey) => void;
    csvUpload?: CsvUploadConfig;
    hasCode?: boolean;
};

export default function GenericDictionaryPage({ tableKey, createTableKey, filterTableKey='generic', fetchData, itemMapper, titleKey,newButtonLabel, onView, onEdit, csvUpload, hasCode = false }: GenericDictionaryPageProps) {
    const { t } = useTranslation(["common", "genericDic"]);
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const tableState = useSelector((state: RootState) => state.historical[tableKey]);
    const currentPage = tableState.currentPage;
    const itemsPerPage = tableState.itemsPerPage;
    const searchText = tableState.searchText;

    const [searchTerm, setSearchTerm] = useState("");
    const [messageError, setMessageError] = useState<string | undefined>("");
    const [isFilterVisible, setIsFilterVisible] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [data, setData] = useState<PaginationList>({
        page: 0,
        size: 0,
        totalPages: 0,
        totalRecords: 0,
        table: tableKey,
        items: []
    });

    const { alert, hideAlert, isVisible, showAlert } = useAlert({ fromLocation: true });
    const { showModal, modalData, error, endDate, closeModal, setError, isLoading, setModalCount, openUploadModal } = useGenericModal();
    const { campaingData } = useGetCampaigns();
    const targetCreateTable = createTableKey ?? tableKey;

    const tableName = data.table ? data.table.charAt(0).toLowerCase() + data.table.slice(1) : "";
    const onlyActiveToggle = useSelector((state: RootState) => state.filters.generic.filterByOnlyActiveRecords);

    const genericActions: Actions = {
        onDelete: async (id: number, table: string) => await deleteData(id, table),
        onActive: async (id: number, table: string) => await activeData(id, table),
        onView: (id: number) => onView ? onView(id) : navigate(`/admin/editGeneric/${data.table}/${id}`, { state: { hasCode } }),
        onEdit: (id: number) => onEdit ? onEdit(id, data.table) : navigate(`/admin/editGeneric/${data.table}/${id}`, { state: { hasCode } })
    };

    const fetchGenericData = useCallback(async (page = currentPage, size = itemsPerPage, search = searchTerm) => {
        const body: PaginationFilterDto = {
            pageNumber: page,
            pageSize: size,
            search,
            tableOrigin: tableKey,
            filters: [],
            filterByOnlyActiveRecords: onlyActiveToggle
        };

        try {
            const response = await fetchData(body);
            const mappedItems = response.items.map((item) => itemMapper
                ? itemMapper(item)
                : {
                    ...item,
                    type: tableKey,
                    nameComponentEdit: tableKey
                });

            setData({
                ...response,
                table: tableKey,
                items: mappedItems
            });
        } catch (err) {
            const errorObj = err as ApiErrorResponse;
            setMessageError(
                errorObj?.message
                || errorObj.message
                || t("common:requestError.ErrorBadRequest")
            );
        }
    }, [currentPage, itemsPerPage, searchTerm, fetchData, itemMapper, tableKey, t, onlyActiveToggle]);

    useEffect(() => {
        fetchGenericData();
    }, [currentPage, itemsPerPage, searchTerm, fetchGenericData]);

    const handleSearchInput = () => {
        setSearchTerm(searchText || "");
        dispatch(setCurrentPage({ table: tableKey, page: 1 }));
    };

    const handleClear = () => {
        dispatch(setCurrentPage({ table: tableKey, page: 1 }));
        dispatch(setSearchText({ table: tableKey, text: "" }));
        setSearchTerm("");
    };

    const handleFileChange = async (file: File | null) => {
        setSelectedFile(file);
        setError("");

        if (!file) return;

        const validationFileExtension = validateFileExtension(file.name);
        const validationFileWeight = validateFileWeight(file.size);

        if (validationFileExtension) {
            setError(t(validationFileExtension));
            return;
        }

        if (validationFileWeight) {
            setError(t(validationFileWeight));
            return;
        }

        const reader = new FileReader();
        reader.onload = async (e) => {
            const text = e.target?.result as string;
            const parsed = Papa.parse(text, {
                skipEmptyLines: true,
                delimiter: ";"
            });
            setModalCount(parsed.data.length);
        };
        reader.readAsText(file);
    };

    const openImportCsvModal = () => {
        if (!csvUpload) return;
        openUploadModal(
            csvUpload.modalTitleKey,
            csvUpload.modalSubtitleKey,
            csvUpload.modalSubtitle2Key ?? ""
        );
    };

    const handleUploadCsv = async () => {
        if (!csvUpload || !selectedFile) return;

        try {
            await csvUpload.onUpload({
                file: selectedFile,
                closeModal,
                refetchData: fetchGenericData,
                showAlert,
                t
            });
        } catch (err) {
            const errorObj = err as ApiErrorResponse;
            closeModal();
            showAlert({
                message: errorObj?.message?.toString() ?? t("common:requestError.ErrorBadRequest"),
                type: "danger",
                duration: 8000
            });
        }
    };

    const resolveNewButtonLabel = () => {
        if (typeof newButtonLabel === "function") {
            return newButtonLabel({ tableKey, tableName, t });
        }

        if (typeof newButtonLabel === "string") {
            return t(newButtonLabel);
        }

        return t("genericDic:claimReopenReasonButtonNew", { tableName: t(`genericDic:${tableName}`).toLowerCase() });
    };

    const handleCreateNew = () => {
        navigate(`/admin/newGeneric/${targetCreateTable}`, { state: { hasCode } });
    };

    const handleToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(setFilterByOnlyActiveRecords({ table: filterTableKey, filterByOnlyActiveRecords: e.target.checked }));
    };

    return (
        <>
            <Row className="mb-2 align-items-center justify-content-between">
                <Col xs={12} md>
                    <h2>{t(titleKey ?? `genericDic:${tableName}`)}</h2>
                </Col>
                <Col xs={12} md="auto" className="d-flex align-items-center justify-content-end mt-2 mt-md-0">
                    <Button
                        variant="primary"
                        style={{ whiteSpace: "nowrap" }}
                        onClick={handleCreateNew}
                    >
                        {resolveNewButtonLabel()}
                    </Button>
                </Col>
            </Row>

            <Row>
                <ModalGlobal<UseGetCampaingsObjectResult>
                    show={showModal}
                    onClose={closeModal}
                    title={modalData.title}
                    contentType={modalData.type}
                    subtitle={modalData.subtitle}
                    saveText={t(csvUpload?.saveTextKey ?? "common:modalActionOK")}
                    closeText={t(csvUpload?.closeTextKey ?? "common:modalActionNOTOK")}
                    error={error}
                    endDate={endDate}
                    isLoading={isLoading}
                    handleFileChange={handleFileChange}
                    onSave={csvUpload ? handleUploadCsv : undefined}
                    optionsMap={csvUpload?.useCampaignOptions ? campaingData : undefined}
                    countValues={modalData.countValues}
                    showOptions={csvUpload?.showOptions}
                />

                <Col className="text-center" xs={12} md={12}>
                    {isVisible && alert && (
                        <AlertMessage
                            message={alert.message}
                            type={alert.type}
                            onClose={hideAlert}
                        />
                    )}
                </Col>

                <Col className="text-center" xs={12} md={12}>
                    {messageError && (
                        <div className="alert alert-danger">
                            {messageError}
                            <button
                                type="button"
                                className="btn-close btn-sm position-absolute top-0 end-0 m-2"
                                aria-label="Close"
                                onClick={() => setMessageError(undefined)}
                            ></button>
                        </div>
                    )}
                </Col>
            </Row>

            <Row className="mb-2">
                <Col xs={12}>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                        <div style={{ flex: 1 }}>
                            <Search
                                searchText={searchText || ""}
                                onChange={(text) => dispatch(setSearchText({ table: tableKey, text }))}
                                onSearch={handleSearchInput}
                                onClear={handleClear}
                            />
                        </div>
                        <Button
                            variant=""
                            onClick={() => setIsFilterVisible(!isFilterVisible)}
                            aria-controls="collapse-filter-id"
                            style={{ border: 0, padding: 0 }}
                        >
                            <img
                                src="/filter.png"
                                alt="Filtar"
                                style={{ width: "40px" }}
                            />
                        </Button>
                    </div>
                </Col>
            </Row>
            <Row>
                <Form>
                    <Form.Check
                        className="my-4"
                        type="switch"
                        id="custom-switch"
                        label={t("common:buttons.toggleActiveRecords")}
                        onChange={handleToggle}
                        checked={onlyActiveToggle}
                    />
                </Form>

            </Row>
            <ListTable
                data={data}
                currentPage={currentPage}
                pageSize={itemsPerPage}
                totalPages={data.totalPages}
                genericActions={genericActions}
                refetchData={fetchGenericData}
                onImportCsv={csvUpload ? openImportCsvModal : undefined}
            />
        </>
    );
}


